import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Github, ExternalLink, Check, Loader2 } from "lucide-react";
import { useIntegrations } from "@/providers/IntegrationsProvider";
import { useServiceOrders } from "@/providers/ServiceOrdersProvider";

interface GitHubConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceOrderId?: string;
  onConnected?: () => void;
}

type DialogStep = "check-installation" | "select-repo" | "connecting";

const getErrorMessage = (context: "install" | "repos" | "connect"): string => {
  const errorMessages = {
    install:
      "Unable to open GitHub installation page. Please try again or contact support if the problem persists.",
    repos:
      "We couldn't load your repositories. Please refresh the page and try again.",
    connect:
      "Failed to connect the repository to this service order. Please try selecting a different repository or try again later.",
  };

  return errorMessages[context];
};

export function GitHubConnectDialog({
  open,
  onOpenChange,
  serviceOrderId,
  onConnected,
}: GitHubConnectDialogProps) {
  const [step, setStep] = useState<DialogStep>("check-installation");
  const [selectedRepo, setSelectedRepo] = useState<string>("");
  const [availableRepos, setAvailableRepos] = useState<string[]>([]);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    hasInstallation,
    getInstallUrl,
    getInstallationByProvider,
    getAvailableRepos,
    connectRepo,
  } = useIntegrations();
  const { fetchServiceOrders } = useServiceOrders();

  const isGitHubInstalled = hasInstallation("github");

  useEffect(() => {
    if (!open) {
      setStep("check-installation");
      setSelectedRepo("");
      setAvailableRepos([]);
      setError(null);
      return;
    }

    if (isGitHubInstalled) {
      setStep("select-repo");
      setIsLoadingRepos(true);
      const installation = getInstallationByProvider("github");
      if (installation) {
        getAvailableRepos("github", installation.id)
          .then(setAvailableRepos)
          .catch(() => {
            setError(getErrorMessage("repos"));
          })
          .finally(() => setIsLoadingRepos(false));
      }
    }
  }, [open, isGitHubInstalled, getInstallationByProvider, getAvailableRepos]);

  const handleInstallGitHub = async () => {
    try {
      const url = await getInstallUrl("github", serviceOrderId!);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError(getErrorMessage("install"));
    }
  };

  const handleConnect = async () => {
    if (!selectedRepo || !serviceOrderId) return;

    setIsConnecting(true);
    setStep("connecting");
    setError(null);

    try {
      const installation = getInstallationByProvider("github");
      if (!installation) throw new Error("Installation not found");

      await connectRepo({
        serviceOrderId,
        provider: "github",
        repo: selectedRepo,
        installationId: installation.id,
      });

      await fetchServiceOrders();
      onConnected?.();
      onOpenChange(false);
    } catch {
      setError(getErrorMessage("connect"));
      setStep("select-repo");
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            {step === "check-installation" && "Connect GitHub"}
            {step === "select-repo" && "Select Repository"}
            {step === "connecting" && "Connecting..."}
          </DialogTitle>
          <DialogDescription>
            {step === "check-installation" &&
              "Install the GitHub App to connect your repository."}
            {step === "select-repo" &&
              "Select a repository from your GitHub account."}
            {step === "connecting" &&
              "Please wait while we connect your repository."}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {step === "check-installation" && !isGitHubInstalled && (
            <motion.div
              key="install"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="py-4"
            >
              <button
                onClick={handleInstallGitHub}
                className="flex items-center gap-4 p-4 rounded-lg border border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 text-left group w-full"
              >
                <div className="w-12 h-12 rounded-xl bg-[#24292e] flex items-center justify-center">
                  <Github className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">GitHub</span>
                    <Badge variant="outline" className="text-xs">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Install App
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Install the GitHub App to continue
                  </p>
                </div>
              </button>
              {error && (
                <p className="text-sm text-red-500 bg-red-500/10 p-3 rounded-lg mt-4">
                  {error}
                </p>
              )}
            </motion.div>
          )}

          {step === "select-repo" && (
            <motion.div
              key="repos"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4 py-4"
            >
              {isLoadingRepos ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">
                    Loading repositories...
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm text-emerald-500 font-medium">
                      GitHub App installed
                    </span>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Repository</label>
                    <Select
                      value={selectedRepo}
                      onValueChange={setSelectedRepo}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a repository" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRepos.map((repo) => (
                          <SelectItem key={repo} value={repo}>
                            {repo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 bg-red-500/10 p-3 rounded-lg">
                      {error}
                    </p>
                  )}

                  <Button
                    onClick={handleConnect}
                    disabled={!selectedRepo || isConnecting}
                    className="w-full"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      "Connect Repository"
                    )}
                  </Button>
                </>
              )}
            </motion.div>
          )}

          {step === "connecting" && (
            <motion.div
              key="connecting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-8"
            >
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              </div>
              <p className="mt-4 text-muted-foreground">
                Connecting repository...
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
